import React from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  LineElement,
  PointElement,
  LineController,
  CategoryScale,
  LinearScale,
  Title,
  Tooltip,
  Legend
);

function MultiLineChart({
  datasets = [],
  labels = [],
  yLabel,
  xLabel,
  chartTitle,
  height = 160,
  showLegend = false,
  tickStep = 5,
  targetLines = [],
  beginAtZero = false,
  maxXTicks,
  pointRadius = 2,
  yLabel2,
  yColor = '#fff',
  y2Color = '#fff',
  tooltipLabelCallback,
}) {
  const data = {
    labels,
    datasets: datasets.map(ds => {
      const baseColor =
        ds.color ||
        (Array.isArray(ds.segmentColors) ? ds.segmentColors.find(Boolean) : undefined) ||
        '#ffffff';

      const datasetConfig = {
        label: ds.label,
        data: ds.data,
        borderColor: baseColor,
        backgroundColor: 'transparent',
        tension: 0.3,
        spanGaps: true,
        borderWidth: ds.borderWidth || 2,
        yAxisID: ds.yAxisID || 'y',
      };

      if (Array.isArray(ds.segmentColors)) {
        datasetConfig.segment = {
          borderColor: ctx => {
            const idx = ctx.p1DataIndex;
            const prevIdx = ctx.p0DataIndex;
            return (
              ds.segmentColors[idx] ||
              ds.segmentColors[prevIdx] ||
              ds.segmentColors.find(Boolean) ||
              baseColor
            );
          },
        };
      }

      if (Array.isArray(ds.pointBackgroundColors)) {
        datasetConfig.pointBackgroundColor = ds.pointBackgroundColors;
      } else if (Array.isArray(ds.pointColors)) {
        datasetConfig.pointBackgroundColor = ds.pointColors;
      }

      if (Array.isArray(ds.pointBorderColors)) {
        datasetConfig.pointBorderColor = ds.pointBorderColors;
      } else if (Array.isArray(ds.pointColors)) {
        datasetConfig.pointBorderColor = ds.pointColors;
      }

      if (ds.pointRadius !== undefined) {
        datasetConfig.pointRadius = ds.pointRadius;
      }

      if (ds.pointHoverRadius !== undefined) {
        datasetConfig.pointHoverRadius = ds.pointHoverRadius;
      }

      if (ds.sources) {
        datasetConfig.sources = ds.sources;
      }

      return datasetConfig;
    }),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: showLegend,
        position: 'top',
        labels: { usePointStyle: true, pointStyle: 'circle' },
      },
      targetLines: { lines: targetLines },
      title: { display: !!chartTitle, text: chartTitle },
      ...(tooltipLabelCallback
        ? { tooltip: { callbacks: { label: tooltipLabelCallback } } }
        : {}),
    },
    elements: { point: { radius: pointRadius } },
    scales: {
      x: {
        title: { display: !!xLabel, text: xLabel },
        ticks: { maxTicksLimit: maxXTicks },
      },
      y: {
        title: { display: !!yLabel, text: yLabel, color: yColor },
        ticks: { stepSize: tickStep, beginAtZero, color: yColor },
        min: beginAtZero ? 0 : undefined,
      },
      ...(yLabel2
        ? {
            y2: {
              position: 'right',
              title: { display: true, text: yLabel2, color: y2Color },
              ticks: { stepSize: tickStep, beginAtZero, color: y2Color },
              grid: { drawOnChartArea: false },
              min: beginAtZero ? 0 : undefined,
            },
          }
        : {}),
    },
  };

  const targetLinePlugin = {
    id: 'targetLines',
    beforeDraw: chart => {
      const { ctx, chartArea: { left, right } } = chart;
      const yScale = chart.scales.y;
      const opts = chart.options.plugins.targetLines || {};
      (opts.lines || []).forEach(tl => {
        const y = yScale.getPixelForValue(tl.value);
        ctx.save();
        ctx.strokeStyle = tl.color;
        ctx.setLineDash([2, 2]);
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(left, y);
        ctx.lineTo(right, y);
        ctx.stroke();
        ctx.restore();
      });
    },
  };

  return (
    <div style={{ height }}>
      <Line data={data} options={options} plugins={[targetLinePlugin]} />
    </div>
  );
}

export default MultiLineChart;
